


















































import { Component, Ref, Vue } from "vue-property-decorator";
import BaseDialogComponent from "@/components/common/BaseDialog.vue";
import { compareMappingImport, MappingImport, MappingImportSource } from "@/utils/PdfTemplateFiles";
import { PdfTemplateEditorRow } from "@/utils/PdfTemplateMapping";

type PdfTemplateImportSource = MappingImportSource;

/**
 * Takes over another template's mapping, as IRES Admin's "Importieren" does: every field of this PDF
 * that has nothing linked gets the source's link; a field linked differently is overwritten only when
 * ticked (none is, by default); the source's fields this PDF lacks are ignored. The page applies the
 * result to its unsaved state; SICHERN saves it.
 *
 * `show()` resolves with the source and the fields to overwrite, or null when cancelled.
 */
@Component({ components: { "base-dialog": BaseDialogComponent } })
export default class PdfTemplateImportDialogComponent extends Vue {
  @Ref("baseDialog")
  baseDialog!: BaseDialogComponent;

  rows: PdfTemplateEditorRow[] = [];
  sources: PdfTemplateImportSource[] = [];
  sourceId: string | null = null;
  overwrite: string[] = [];

  private resolve: ((result: { source: PdfTemplateImportSource; overwrite: string[] } | null) => void) | null = null;

  show(rows: PdfTemplateEditorRow[], sources: PdfTemplateImportSource[]) {
    this.rows = rows;
    this.sources = sources;
    this.sourceId = null;
    this.overwrite = [];
    this.baseDialog.show({
      title: this.$t("admin.pdfTemplates.files.import.title").toString(),
      positiveButtonText: this.$t("common.apply").toString(),
      negativeButtonText: this.$t("common.abort").toString(),
      maxWidth: 640,
      scrollable: true,
      persistent: true,
      closeOnSave: false,
    });

    return new Promise<{ source: PdfTemplateImportSource; overwrite: string[] } | null>((resolve) => (this.resolve = resolve));
  }

  /** This scope's templates, then the shared ones, under a heading each. */
  get sourceItems(): Array<{ header: string } | PdfTemplateImportSource> {
    const group = (name: "own" | "shared") => {
      const entries = this.sources.filter((source) => source.group === name);
      return entries.length > 0
        ? [{ header: this.$t(`admin.pdfTemplates.files.import.groups.${name}`).toString() }, ...entries]
        : [];
    };
    return [...group("own"), ...group("shared")];
  }

  get source(): PdfTemplateImportSource | null {
    return this.sources.find((candidate) => candidate.id === this.sourceId) ?? null;
  }

  get plan(): MappingImport | null {
    const source = this.source;
    return source ? compareMappingImport(this.rows, source.mappings) : null;
  }

  get canApply(): boolean {
    const plan = this.plan;
    return plan !== null && (plan.fresh.length > 0 || this.overwrite.length > 0);
  }

  onSourceChanged(sourceId: string | null) {
    this.sourceId = sourceId;
    this.overwrite = [];
  }

  onApply() {
    const source = this.source;
    if (source && this.canApply) {
      this.close({ source, overwrite: [...this.overwrite] });
    }
  }

  /** BaseDialog has already closed itself on ABBRECHEN. */
  onCancel() {
    this.resolve?.(null);
    this.resolve = null;
  }

  close(result: { source: PdfTemplateImportSource; overwrite: string[] } | null) {
    this.baseDialog.isShowing = false;
    this.resolve?.(result);
    this.resolve = null;
  }
}
